import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { FeedbackReaction } from "../../types/frontend";

/**
 * Props for the FeedbackPopover component.
 *
 * @property {number} score - The feedback score (1 for thumbs up, 0 for thumbs down).
 * @property {string} traceId - The unique identifier for the chat trace to associate feedback with.
 * @property {FeedbackReaction} reaction - The type of feedback reaction (up or down).
 * @property {function} handleSubmitFeedback - Callback to submit feedback, including traceId, reaction, optional comment, and optional reasons.
 * @property {function} onClose - Callback to close the popover.
 */
interface FeedbackPopoverProps {
  score: number;
  traceId: string;
  reaction: FeedbackReaction;
  handleSubmitFeedback: (
    traceId: string,
    reaction: FeedbackReaction,
    comment?: string,
    reasons?: string[]
  ) => void;
  onClose: () => void;
}

const MAX_COMMENT_LENGTH = 100;

const FeedbackPopover: React.FC<FeedbackPopoverProps> = ({
  reaction,
  traceId,
  handleSubmitFeedback,
  onClose,
}) => {
  const theme = useTheme();

  // Define negative and positive feedback options
  const NEGATIVE_REASONS = [
    "Took too long to answer",
    "Not factually correct",
    "Sources aren't relevant",
    "Didn't follow instructions",
    "Needed more detail",
    "Other",
  ];

  const POSITIVE_REASONS = [
    "Quick and timely response",
    "Factually correct and accurate",
    "Relevant and useful sources",
    "Followed instructions well",
    "Detailed and thorough response",
    "Other",
  ];

  // Dynamically set reasons based on the reaction
  const REASONS =
    reaction === FeedbackReaction.Up ? POSITIVE_REASONS : NEGATIVE_REASONS;

  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [submitEnabled, setSubmitEnabled] = useState(false);

  /**
   * Toggles the selection of a reason.
   *
   * @param reason - The reason to toggle.
   */
  const handleReasonToggle = (reason: string) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  };

  /**
   * Updates the submit button's enabled state based on the selected reasons and comment.
   */
  useEffect(() => {
    const otherSelected = selectedReasons.includes("Other");
    const validOther = otherSelected ? comment.trim().length > 0 : true;
    const validReasons =
      selectedReasons.length > 0 || comment.trim().length > 0;
    setSubmitEnabled(validReasons && validOther);
  }, [selectedReasons, comment]);

  /**
   * Handles the submission of feedback.
   */
  const handleSubmit = async () => {
    handleSubmitFeedback(traceId, reaction, comment, selectedReasons);
    onClose();
  };

  return (
    <Box
      sx={{
        p: 3,
        width: 600,
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        backgroundColor: "white",
        position: "relative",
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: theme.palette.grey[500],
          "&:hover": { color: theme.palette.grey[700] },
        }}
        disableRipple
      >
        <CloseIcon />
      </IconButton>

      {/* Feedback Title */}
      <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
        {reaction === FeedbackReaction.Down
          ? "Why wasn't this helpful?"
          : "Why was this helpful?"}
      </Typography>

      {/* Feedback Description */}
      <Typography
        variant="body2"
        sx={{ mb: 2, color: theme.palette.text.secondary }}
      >
        Select one or more reasons and provide additional comments if needed.
      </Typography>

      {/* Feedback Reasons */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        {REASONS.map((reason) => {
          const isSelected = selectedReasons.includes(reason);
          return (
            <Chip
              key={reason}
              label={reason}
              onClick={() => handleReasonToggle(reason)}
              clickable
              sx={{
                backgroundColor: isSelected
                  ? theme.palette.primary.main
                  : theme.palette.grey[300],
                color: isSelected ? "white" : "black",
                "&:hover": {
                  backgroundColor: isSelected
                    ? theme.palette.primary.dark
                    : theme.palette.grey[400],
                },
                "& .MuiTouchRipple-root": {
                  display: "none",
                },
              }}
            />
          );
        })}
      </Box>

      {/* Feedback Comment */}
      <TextField
        placeholder="Optional: Let us know your opinion"
        fullWidth
        multiline
        value={comment}
        onChange={(e) =>
          setComment(e.target.value.slice(0, MAX_COMMENT_LENGTH))
        }
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
          },
        }}
      />

      {/* Feedback Submit Button */}
      <Button
        variant="contained"
        fullWidth
        onClick={handleSubmit}
        disabled={!submitEnabled}
        sx={{
          textTransform: "none",
          fontWeight: "bold",
          py: 1.5,
        }}
        disableRipple
      >
        Submit Feedback
      </Button>
    </Box>
  );
};

export default FeedbackPopover;
